import { AuditTrailReportEntity } from "domain/entity/AuditTrailReport/AuditTrailReportEntity";
import { FinancialReportEntity } from "domain/entity/FinancialReport/FinancialReportEntity";
import { GeneralCsvReportEntity } from "domain/entity/GeneralCsvReport/GeneralCsvReportEntity";
import { GeneralReportEntity } from "domain/entity/GeneralReport/GeneralReportEntity";
import { AuditTrailReportConstant } from "presentation/constant/AuditTrailReport/AuditTrailReportConstant";
import { FinancialReportConstant } from "presentation/constant/FinancialReport/FinancialReportConstant";
import { GeneralReportConstant } from "presentation/constant/GeneralReport/GeneralReportConstant";

interface DateFieldValidation {
  from: keyof (GeneralReportEntity | AuditTrailReportEntity | FinancialReportEntity);
  to: keyof (GeneralReportEntity | AuditTrailReportEntity | FinancialReportEntity);
  fieldLabel1: string;
  fieldLabel2: string;
  compareAdapters?: DateCompareAdapters[],
}

/*interface ReportValidationConfig {
  requiredFields: (keyof (GeneralReportEntity | AuditTrailReportEntity | FinancialReportEntity | GeneralCsvReportEntity))[],
  specialValidations?: (entity: (GeneralReportEntity | AuditTrailReportEntity | FinancialReportEntity | GeneralCsvReportEntity)) => { [x: string]: string } | null,
  customCondition?: (entity: (GeneralReportEntity | AuditTrailReportEntity | FinancialReportEntity | GeneralCsvReportEntity)) => boolean,
  customMessage?: string
}
*/
type ReportEntity = GeneralReportEntity | AuditTrailReportEntity | FinancialReportEntity | GeneralCsvReportEntity;

interface ReportValidationConfig {
  requiredFields: (keyof ReportEntity)[] | ((entity: ReportEntity) => (keyof ReportEntity)[]);
  specialValidations?: (entity: ReportEntity) => { [x: string]: string } | null;
  customCondition?: (entity: ReportEntity) => boolean;
  customMessage?: string;
}

const ERROR_MESSAGES = {
  MISSING: "Missing.",
  // MANDATORY_CHECK: "Please input the missing value.",
  PROPOSAL_SAME: "Proposal (From) cannot be the same as Proposal (To).",
  DATE_ORDER: {
    isSameDay: (fieldLabel1: string, fieldLabel2: string) => '',
    isNotSameDay: (fieldLabel1: string, fieldLabel2: string) => `${fieldLabel1} and ${fieldLabel2} should be on the same day.`,
    isAfter: (fieldLabel1: string, fieldLabel2: string) => `${fieldLabel2} should be later than or equals to ${fieldLabel1}.`,
    isBefore: (fieldLabel1: string, fieldLabel2: string) => '',
    isSameOrAfter: (fieldLabel1: string, fieldLabel2: string) => '',
    isSameOrBefore: (fieldLabel1: string, fieldLabel2: string) => ''
  },
  YEAR_MONTH_ORDER: "To Year/Month should be later than or equals to From Year/Month"
};

const commonRequiredFields = ["printer", "fileLocation"];
const REPORT_VALIDATION_CONFIGS: Record<string, ReportValidationConfig> = {
  "Daily Yard Inventory": {
    requiredFields: ["opsLine", "month", "year"]
  },
  "Free Pool Volume": {
    requiredFields: ["opsLine", "month", "year"]
  },
  "Output VAT Report": {
    requiredFields: ["docDateFrom", "docDateTo"]
  },
  "Billing By Client and Concept": {
    requiredFields: [],
    customCondition: (entity: (GeneralReportEntity | AuditTrailReportEntity | FinancialReportEntity | GeneralCsvReportEntity)) =>
      !!((entity.docDateFrom && entity.docDateTo) || (entity.glDateFrom && entity.glDateTo)),
    customMessage: "Please input either Doc Date or GL Date or both in the criteria."
  },
  "Billing Comparative": {
    requiredFields: [],
    customCondition: (entity: (GeneralReportEntity | AuditTrailReportEntity | FinancialReportEntity | GeneralCsvReportEntity)) =>
      !!((entity.docDateFrom && entity.docDateTo) || (entity.glDateFrom && entity.glDateTo)),
    customMessage: "Please input either Doc Date or GL Date or both in the criteria."
  },
  "Comparative between TEUs": {
    requiredFields: ["p1OperationDateFrom", "p1OperationDateTo", "p2OperationDateFrom", "p2OperationDateTo"]
  },
  "Movement List": {
    requiredFields: ["vslOperationDateFrom", "vslOperationDateTo"]
  },
  "Movements Per Line": {
    requiredFields: ["vslOperationDateFrom", "vslOperationDateTo"]
  },
  "Movements Per Ship Owner": {
    requiredFields: ["vslOperationDateFrom", "vslOperationDateTo"]
  },
  "Output Statistics": {
    requiredFields: ["etdFrom", "etdTo"]
  },
  "Price Changes List": {
    requiredFields: ["chargeOnCompany", "effectiveDateFrom", "effectiveDateTo"]
  },
  "Rail Movement": {
    requiredFields: ["operationDateFrom", "operationDateTo"]
  },
  "Storage and Reefer Charge Simulation Report": {
    requiredFields: ["simulationDate"]
  },
  "TEUs Billed": {
    requiredFields: [],
    customCondition: (entity: (GeneralReportEntity | AuditTrailReportEntity | FinancialReportEntity | GeneralCsvReportEntity)) =>
      !!((entity.docDateFrom && entity.docDateTo) || (entity.glDateFrom && entity.glDateTo)),
    customMessage: "Please input either Doc Date or GL Date or both in the criteria."
  },
  "Vessel movements report": {
    requiredFields: ["vslOperationDateFrom", "vslOperationDateTo"]
  },
  "Billing Statistics Report": {
    requiredFields: ["settlementFromDate", "settlementToDate"]
  },
  "Cash Reconciliation": {
    requiredFields: ["collFromDate", "collToDate"]
  },
  "Daily Invoice Summary": {
    requiredFields: ["docDateFrom", "docDateTo"]
  },
  "Invoice Register": {
    requiredFields: ["docDateFrom", "docDateTo"]
  },
  "Summary of Customer Monthly Balance": {
    requiredFields: ["docDateFrom", "docDateTo"]
  },
  "Tax Report": {
    requiredFields: ["docDateFrom", "docDateTo"]
  },
  "Empty Pool Container List Report": {
    /*requiredFields: ((entity: GeneralReportEntity | AuditTrailReportEntity | FinancialReportEntity | GeneralCsvReportEntity) => {
      return entity.operatingUnit !== "SAPT" && entity.operatingUnit !== "KICT"
        ? ["printer", "year", "month"]
        : ["printer"];
    }) as unknown as (string | number)[],*/
    requiredFields: (entity: ReportEntity): (keyof ReportEntity)[] => {
      return entity.operatingUnit !== "SAPT" && entity.operatingUnit !== "KICT"
        ? ["printer", "year", "month"] as (keyof ReportEntity)[]
        : ["printer"] as (keyof ReportEntity)[];
    },
    //customCondition: (entity) => entity.operatingUnit !== "SAPT" && entity.operatingUnit !== "KICT"    
  },
  "Full Pool Container List Report": {
    /*requiredFields: ((entity: GeneralReportEntity | AuditTrailReportEntity | FinancialReportEntity | GeneralCsvReportEntity) => {
      return entity.operatingUnit !== "SAPT" && entity.operatingUnit !== "KICT"
        ? ["printer", "year", "month"]
        : ["printer"];
    }) as unknown as (string | number)[],*/
    requiredFields: (entity: ReportEntity): (keyof ReportEntity)[] => {
      return entity.operatingUnit !== "SAPT" && entity.operatingUnit !== "KICT"
        ? ["printer", "year", "month"] as (keyof ReportEntity)[]
        : ["printer"] as (keyof ReportEntity)[];
    },
    //customCondition: (entity) => entity.operatingUnit !== "SAPT" && entity.operatingUnit !== "KICT"    
  },
  "Exception Report": {
    requiredFields: ["exceptionType"]
  },
  "Tariff Adjustment Report": {
    requiredFields: ["companyCodeFrom", "companyCodeTo", "proposalFrom", "proposalTo"],
    specialValidations: (entity) => {
      if (entity.companyCodeFrom === entity.companyCodeTo &&
        entity.proposalFrom === entity.proposalTo) {
        return { validateFailed: ERROR_MESSAGES.PROPOSAL_SAME };
      }
      return null;
    }
  },
  "Container Un-Billed / Billed THC Report": {
    requiredFields: ["vesselArrivalFromDate", "vesselArrivalToDate"]
  },
  "LPA Account Analysis": {
    requiredFields: ["transactionFromDate", "transactionTomDate"]
  },
  "LPA Daily Deposit Report": {
    requiredFields: ["transactionFromDate", "transactionTomDate", "activeInd"]
  },
  "Volume Discount Report": {
    requiredFields: ["referenceFromDate", "referenceTomDate", "dateFrom", "dateTo"]
  },
  "Invoice Summary Report": {
    requiredFields: ["dateFrom", "dateTo"]
  },
  "Credit Notes Summary Report": {
    requiredFields: ["dateFrom", "dateTo", "type"]
  },
  "Let Pass Account Monthly Statement": {
    requiredFields: ["dateFrom", "dateTo", "companyCode"]
  },
  "Sales Tax Report": {
    requiredFields: ["dateFrom", "dateTo", "companyType"]
  },
  "Waiver Report": {
    requiredFields: ["approvedFromDate", "approvedTomDate"]
  },
  "Revenue Analysis Per Shipping Line Per Tariff Item": {
    requiredFields: ["dateFrom", "dateTo"]
  },
  "CC Invoice/Credit Note Summary Report": {
    requiredFields: ["dateFrom", "dateTo", "type"]
  },
};

const GENERAL_REPORT_CONSTANT = GeneralReportConstant.Table;
const AUDIT_TRAIL_REPORT_CONSTANT = AuditTrailReportConstant.Table;
const FINANCIAL_REPORT_CONSTANT = FinancialReportConstant.Table;

const dateCompareAdapter = {
  isSameDay: (date1: Date, date2: Date) => {
    return date1.getFullYear() === date2.getFullYear() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getDate() === date2.getDate();
  },
  isNotSameDay: (date1: Date, date2: Date) => {
    return !(date1.getFullYear() === date2.getFullYear() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getDate() === date2.getDate());
  },
  isAfter: (date1: Date, date2: Date) => date1 > date2,
  isBefore: (date1: Date, date2: Date) => date1 < date2,
  isSameOrAfter: (date1: Date, date2: Date) => date1 >= date2,
  isSameOrBefore: (date1: Date, date2: Date) => date1 <= date2
};
type DateCompareAdapters = keyof typeof dateCompareAdapter;

const DATE_FIELD_VALIDATIONS: DateFieldValidation[] = [
  { from: "transactionDateFrom", to: "transactionDateTo", fieldLabel1: AUDIT_TRAIL_REPORT_CONSTANT.TRANS_DATE_FROM, fieldLabel2: AUDIT_TRAIL_REPORT_CONSTANT.TRANS_DATE_TO },
  { from: "settlementFromDate", to: "settlementToDate", fieldLabel1: GENERAL_REPORT_CONSTANT.SETTLEMENT_DATE_FROM, fieldLabel2: GENERAL_REPORT_CONSTANT.SETTLEMENT_DATE_TO },
  { from: "docDateFrom", to: "docDateTo", fieldLabel1: GENERAL_REPORT_CONSTANT.DOC_DATE_FROM, fieldLabel2: GENERAL_REPORT_CONSTANT.DOC_DATE_TO },
  { from: "opsDateFrom", to: "opsDateTo", fieldLabel1: GENERAL_REPORT_CONSTANT.OPS_DATE_FROM, fieldLabel2: GENERAL_REPORT_CONSTANT.OPS_DATE_TO },
  { from: "effectiveDateFrom", to: "effectiveDateTo", fieldLabel1: GENERAL_REPORT_CONSTANT.EFFECTIVE_DATE_FROM, fieldLabel2: GENERAL_REPORT_CONSTANT.EFFECTIVE_DATE_TO },
  { from: "expiryDateFrom", to: "expiryDateTo", fieldLabel1: GENERAL_REPORT_CONSTANT.EXPIRY_DATE_FROM, fieldLabel2: GENERAL_REPORT_CONSTANT.EXPIRY_DATE_TO },
  { from: "exceptionDateFrom", to: "exceptionDateTo", fieldLabel1: GENERAL_REPORT_CONSTANT.EXCEPTION_DATE_FROM, fieldLabel2: GENERAL_REPORT_CONSTANT.EXCEPTION_DATE_TO },
  { from: "glDateFrom", to: "glDateTo", fieldLabel1: GENERAL_REPORT_CONSTANT.GL_DATE_FROM, fieldLabel2: GENERAL_REPORT_CONSTANT.GL_DATE_TO },
  { from: "referenceFromDate", to: "referenceTomDate", fieldLabel1: GENERAL_REPORT_CONSTANT.REFERENCE_DATE_FROM, fieldLabel2: GENERAL_REPORT_CONSTANT.REFERENCE_DATE_TO },
  { from: "approvedFromDate", to: "approvedTomDate", fieldLabel1: GENERAL_REPORT_CONSTANT.APPROVED_DATE_FROM, fieldLabel2: GENERAL_REPORT_CONSTANT.APPROVED_DATE_TO },
  { from: "transactionFromDate", to: "transactionTomDate", fieldLabel1: GENERAL_REPORT_CONSTANT.TRANSACTION_DATE_FROM, fieldLabel2: GENERAL_REPORT_CONSTANT.TRANSACTION_DATE_TO },
  { from: "dateFrom", to: "dateTo", fieldLabel1: GENERAL_REPORT_CONSTANT.DATE_FROM, fieldLabel2: GENERAL_REPORT_CONSTANT.DATE_TO },
  { from: "vesselArrivalFromDate", to: "vesselArrivalToDate", fieldLabel1: GENERAL_REPORT_CONSTANT.VESSEL_ARRIVAL_FROM, fieldLabel2: GENERAL_REPORT_CONSTANT.VESSEL_ARRIVAL_TO },
  {
    from: "collFromDate", to: "collToDate", fieldLabel1: GENERAL_REPORT_CONSTANT.COLL_DATE_FROM, fieldLabel2: GENERAL_REPORT_CONSTANT.COLL_DATE_TO,
    compareAdapters: ["isNotSameDay", "isAfter"]
  },
  { from: "p1OperationDateFrom", to: "p1OperationDateTo", fieldLabel1: "VSL Start Operation Date1 (from)", fieldLabel2: "VSL Start Operation Date1 (To)" },
  { from: "p2OperationDateFrom", to: "p2OperationDateTo", fieldLabel1: "VSL Start Operation Date2 (from)", fieldLabel2: "VSL Start Operation Date2 (To)" },
  { from: "vslOperationDateFrom", to: "vslOperationDateTo", fieldLabel1: FINANCIAL_REPORT_CONSTANT.VSL_OPERATION_DATE_FROM, fieldLabel2: FINANCIAL_REPORT_CONSTANT.VSL_OPERATION_DATE_TO },
  { from: "operationDateFrom", to: "operationDateTo", fieldLabel1: FINANCIAL_REPORT_CONSTANT.OPERATION_DATE_FROM, fieldLabel2: FINANCIAL_REPORT_CONSTANT.OPERATION_DATE_TO },
  { from: "etdFrom", to: "etdTo", fieldLabel1: FINANCIAL_REPORT_CONSTANT.ETD_DATE_FROM, fieldLabel2: FINANCIAL_REPORT_CONSTANT.ETD_DATE_TO },
];

export const reportCheckFieldMandatory = (
  entity: (GeneralReportEntity | AuditTrailReportEntity | FinancialReportEntity | GeneralCsvReportEntity),
  operatingUnit: string
): { [x: string]: string } => {
  let allValResult: { [x: string]: string } = {};
  entity.operatingUnit = operatingUnit;
  /*const validateRequiredFields = (fields: (keyof (GeneralReportEntity | AuditTrailReportEntity | FinancialReportEntity | GeneralCsvReportEntity))[]) => {
    fields.forEach(field => {
      if (!entity[field]) {
        allValResult[field] = ERROR_MESSAGES.MISSING;
        // allValResult.mandatoryCheckFail = ERROR_MESSAGES.MANDATORY_CHECK;
      }
    });
    };*/
  const validateRequiredFields = (fieldsOrFn: (keyof ReportEntity)[] | ((entity: ReportEntity) => (keyof ReportEntity)[])) => {
    let fields: (keyof ReportEntity)[] = [];
    if (typeof fieldsOrFn === 'function') {
      fields = fieldsOrFn(entity);
    } else {
      fields = fieldsOrFn;
    }
    fields.forEach(field => {
      if (!entity[field]) {
        allValResult[field] = ERROR_MESSAGES.MISSING;
        // allValResult.mandatoryCheckFail = ERROR_MESSAGES.MANDATORY_CHECK;
      }
    });
  };

  const validateDateOrder = (from: keyof (GeneralReportEntity | AuditTrailReportEntity | FinancialReportEntity | GeneralCsvReportEntity), to: keyof (GeneralReportEntity | AuditTrailReportEntity | FinancialReportEntity | GeneralCsvReportEntity),
    fieldLabel1: string, fieldLabel2: string,
    compareAdapters: DateCompareAdapters[] = ["isAfter"]) => {
    const fromDate = entity[from];
    const toDate = entity[to];

    if (!(fromDate instanceof Date) || !(toDate instanceof Date) || isNaN(fromDate.getTime()) || isNaN(toDate.getTime())) {
      return;
    }

    if (compareAdapters) {
      compareAdapters?.forEach(compareAdapter => {
        if (dateCompareAdapter[compareAdapter]) {
          const comparisonFn = dateCompareAdapter[compareAdapter];
          if (comparisonFn(fromDate, toDate)) {
            const key: keyof typeof ERROR_MESSAGES.DATE_ORDER = compareAdapter;

            allValResult.validateFailed = ERROR_MESSAGES.DATE_ORDER[key](fieldLabel1, fieldLabel2);
          }
        }
      })
    }
  };

  const validateYearMonthOrder = () => {
    if (entity.fromYear && entity.fromMonth && entity.toYear && entity.toMonth) {
      if (entity.fromYear > entity.toYear ||
        (entity.fromYear === entity.toYear && entity.fromMonth > entity.toMonth)) {
        allValResult.validateFailed = ERROR_MESSAGES.YEAR_MONTH_ORDER;
      }
    }
  };

  const commonValidateRequiredFields = () => {
    validateRequiredFields(commonRequiredFields);
  };

  const reportConfig = REPORT_VALIDATION_CONFIGS[entity.reportType];
  commonValidateRequiredFields();
  if (reportConfig) {
    const shouldValidate = !reportConfig.customCondition ||
      reportConfig.customCondition(entity);

    if (shouldValidate) {
      validateRequiredFields(reportConfig.requiredFields);

      if (reportConfig.specialValidations) {
        const specialErrors = reportConfig.specialValidations(entity);
        if (specialErrors) {
          Object.assign(allValResult, specialErrors);
        }
      }
    } else {
      if (reportConfig.customMessage) {
        Object.assign(allValResult, { validateFailed: reportConfig.customMessage });
      }
    }
  }

  DATE_FIELD_VALIDATIONS?.forEach(({ from, to, fieldLabel1, fieldLabel2, compareAdapters }) => {
    validateDateOrder(from, to, fieldLabel1, fieldLabel2, compareAdapters);
  });

  validateYearMonthOrder();

  return allValResult;
};

/*export const processReportMissingFields = (
  entity: (GeneralReportEntity | AuditTrailReportEntity | FinancialReportEntity | GeneralCsvReportEntity)
): { [x: string]: string } => {
  let allValResult: { [x: string]: string } = {};

  if (entity.reportType) {
    const newReqFields: any[] = [...commonRequiredFields];
    const reportConfig = REPORT_VALIDATION_CONFIGS[entity.reportType];

    if (reportConfig) {
      newReqFields.push(...reportConfig.requiredFields ?? []);
    }

    newReqFields?.forEach(field => {
      if (entity[field]) {
        allValResult = { ...allValResult, [field]: '' };
      }
    });
  }

  return allValResult;
}*/

export const processReportMissingFields = (
  entity: GeneralReportEntity | AuditTrailReportEntity | FinancialReportEntity | GeneralCsvReportEntity
): { [x: string]: string } => {
  let allValResult: { [x: string]: string } = {};

  if (!entity.reportType) {
    return allValResult;
  }

  const reportConfig = REPORT_VALIDATION_CONFIGS[entity.reportType];

  let newReqFields: (keyof typeof entity)[] = [...commonRequiredFields];

  if (reportConfig) {
    const { requiredFields } = reportConfig;

    const resolvedFields = typeof requiredFields === 'function'
      ? requiredFields(entity)
      : requiredFields;

    newReqFields = [
      ...newReqFields,
      ...(resolvedFields || [])
    ];
  }


  newReqFields.forEach((field) => {
    if (entity[field]) {
      allValResult = { ...allValResult, [field]: '' };
    }
  });

  return allValResult;
};

export const isDocDateShowOptional = (reportType: string): boolean => {
  let ret = true;
  switch (reportType) {
    case "Daily Invoice Summary":
      ret = false;
      break;
    case "Invoice Register":
      ret = false;
      break;
    case "Summary of Customer Monthly Balance":
      ret = false;
      break;
    case "Tax Report":
      ret = false;
      break;
    default:
      break;
  }
  return ret;
}

export const isCompanyCodeShowOptional = (reportType: string): boolean => {
  let ret = true;
  switch (reportType) {
    case "Let Pass Account Monthly Statement":
      ret = false;
      break;
  }
  return ret;
}

export const isDateFromToShowOptional = (reportType: string): boolean => {
  let ret = true;
  switch (reportType) {
    case "Credit Notes Summary Report":
      ret = false;
      break;
    case "Invoice Summary Report":
      ret = false;
      break;
    case "Let Pass Account Monthly Statement":
      ret = false;
      break;
    case "Sales Tax Report":
      ret = false;
      break;
    case "Volume Discount Report":
      ret = false;
      break;
    //AQCT/OICT/RSCT
    case "Revenue Analysis Per Shipping Line Per Tariff Item":
      ret = false;
      break;
  }
  return ret;
}

/*export const financialReportCheckDocAndGLDateField = (entity: FinancialReportEntity): boolean => {


    if (entity.docDateFrom && entity.docDateTo && entity.glDateFrom && entity.glDateTo) {
        return true;
    }
    if (entity.docDateFrom && entity.docDateTo && !entity.glDateFrom && !entity.glDateTo) {
        return true;
    }
    if (!entity.docDateFrom && !entity.docDateTo && entity.glDateFrom && entity.glDateTo) {
        return true;
    }
    return false;

}
export const financialReportCheckFieldMandatory = (entity: FinancialReportEntity): string | null => {
    let ret = null;
    switch (entity.reportType) {
        case "Billing By Client and Concept":
            if (!financialReportCheckDocAndGLDateField(entity)) {
                ret = "Please input either Doc Date or GL Date or both in the criteria.";
            }
            break;
        case "Billing Comparative":
            if (!financialReportCheckDocAndGLDateField(entity)) {
                ret = "Please input either Doc Date or GL Date or both in the criteria.";
            }
            break;
        case "Comparative between TEUs":
            if (!entity.p1OperationDateFrom || !entity.p1OperationDateTo || !entity.p2OperationDateFrom || !entity.p2OperationDateTo) {
                ret = "VSL Start Operation Date From, VSL Start Operation Date To is mandatory.";
            }
            break;
        case "Movement List":
            if (!entity.vslOperationDateFrom || !entity.vslOperationDateTo) {
                ret = "VSL Start Operation Date From, VSL Start Operation Date To is mandatory.";
            }
            break;
        case "Movements Per Line":
            if (!entity.vslOperationDateFrom || !entity.vslOperationDateTo) {
                ret = "VSL Start Operation Date From, VSL Start Operation Date To is mandatory.";
            }
            break;
        case "Movements Per Ship Owner":
            if (!entity.vslOperationDateFrom || !entity.vslOperationDateTo) {
                ret = "VSL Start Operation Date From, VSL Start Operation Date To is mandatory.";
            }
            break;
        case "Output Statistics":
            if (!entity.etdFrom || !entity.etdTo) {
                ret = "ETD From, ETD To is mandatory.";
            }
            break;
        case "Price Changes List":
            if (!entity.effectiveDateTo) {
                ret = "Effective Date (To) is mandatory.";
            }
            if (!entity.effectiveDateFrom) {
                ret = "Effective Date (From) is mandatory.";
            }
            if (!entity.chargeOnCompany) {
                ret = "Charge-On Company is mandatory.";
            }
            break;
        case "Rail Movement":
            if (!entity.operationDateFrom || !entity.operationDateTo) {
                ret = "Operation Date From, Operation Date To is mandatory.";
            }
            break;
        case "Revenue By Tariff And Customer":
            if (!entity.year || !entity.month || !entity.printer) {
                ret = "Year and Month is mandatory.";
            }
            break;
        case "TEUs Billed":
            if (!financialReportCheckDocAndGLDateField(entity)) {
                ret = "Please input either Doc Date or GL Date or both in the criteria.";
            }
            break;
        case "Vessel movements report":
            if (!entity.vslOperationDateFrom || !entity.vslOperationDateTo) {
                ret = "VSL Start Operation Date From, VSL Start Operation Date To is mandatory.";
            }
            break;
        default:
            break;
    }
    if (entity.docDateFrom && entity.docDateTo && entity.docDateFrom > entity.docDateTo) {
        ret = "Doc Date (To) should be later than or equals to Doc Date (From).";
    }
    if (entity.glDateFrom && entity.glDateTo && entity.glDateFrom > entity.glDateTo) {
        ret = "GL Date (To) should be later than or equals to GL Date (From).";
    }
    if (entity.etdFrom && entity.etdTo && entity.etdFrom > entity.etdTo) {
        ret = "ETD (To) should be later than or equals to ETD (From).";
    }
    if (entity.p1OperationDateFrom && entity.p1OperationDateTo && entity.p1OperationDateFrom > entity.p1OperationDateTo) {
        ret = "VSL Start Operation Date1 (To) should be later than or equal to VSL Start Operation Date1 (From).";
    }
    if (entity.p2OperationDateFrom && entity.p2OperationDateTo && entity.p2OperationDateFrom > entity.p2OperationDateTo) {
        ret = "VSL Start Operation Date2 (To) should be later than or equal to VSL Start Operation Date2 (From).";
    }
    if (entity.vslOperationDateFrom && entity.vslOperationDateTo && entity.vslOperationDateFrom > entity.vslOperationDateTo) {
        ret = "VSL Start Operation Date (To) should be later than or equal to VSL Start Operation Date (From).";
    }
    if (entity.effectiveDateFrom && entity.effectiveDateTo && entity.effectiveDateFrom > entity.effectiveDateTo) {
        ret = "Effective Date (To) should be later than or equals to Effective Date (From).";
    }
    if (entity.operationDateFrom && entity.operationDateTo && entity.operationDateFrom > entity.operationDateTo) {
        ret = "Operation Date (To) should be later than or equals to Operation Date (From).";
    }
    return ret;
}*/

export const isActiveIndShowOptional = (reportType: string): boolean => {
  let ret = true;
  switch (reportType) {
    case "LPA Daily Deposit Report":
      ret = false;
      break;
  }
  return ret;
}


export const isDateTime = (reportType: string): boolean => {
  let ret = true;
  switch (reportType) {
    case "LPA Account Analysis":
      ret = false;
      break;
  }
  return ret;
}